import React, { useState, useEffect, useMemo } from "react";
import styles from "../KpiForm.module.sass";
import { Dropdown, TextArea, TextInput } from "colbi_web_ui/lib/components/inputs";
import { SovosIcon } from "mosaic-react";
import { HighlightOff } from "mosaic-react-icons";
import { parseJson } from "../../../../utils";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";

const CommandsTab = ({ Content, Metadata, querySelectors, handleChange, operationTypes }) => {
    const [eventType, setEventType] = useState("");
    const [refs, setRefs] = useState({});
    const [scroll, setScroll] = useState(false);
    const [metadataFormatted, setMetadataFormatted] = useState("{}");
    const { i18n } = useGlobalValue();

    const { items: sqlSelectors } = querySelectors || {};
    const parsedMetadata = metadataJsonToArray(parseJson(metadataFormatted));
    const parsedContent = parseJson(Content);
    const kpiMetadataItems = buildKpiMetadataItems(parsedMetadata);
    const kpiContentItems = buildKpiContentItems(parsedContent, sqlSelectors);

    useEffect(() => {
        if (parsedMetadata && parsedMetadata.length) {
            setRefs(
                (parsedMetadata || []).reduce((acc, _, index) => {
                    acc[index] = React.createRef();
                    return acc;
                }, {}),
            );
        }
        if (eventType === "scrollToContainer") {
            setScroll(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadataFormatted]);

    useEffect(() => {
        if (scroll && eventType === "scrollToContainer") {
            scrollToItem(parsedMetadata.length - 1);
        }
        setScroll(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scroll]);

    useMemo(() => {
        try {
            if (Metadata) {
                setMetadataFormatted(JSON.stringify(JSON.parse(Metadata)));
            } else {
                setMetadataFormatted("{}");
            }
        } catch (e) {
            console.log(e);
        }
    }, [Metadata]);

    function metadataJsonToArray(jsonInput) {
        return Object.entries(jsonInput).map(([key, value], index) => {
            return {
                key: `${key.split("_")[0]}_${index}`,
                ...value,
            };
        });
    }

    function metadataArrayToJson(inputArray) {
        return inputArray.reduce((acc, config, index) => {
            const { key, ...rest } = config;

            acc[`${key.split("_")[0]}_${index}`] = { ...rest };
            return acc;
        }, {});
    }

    const scrollToItem = (id) => {
        return refs[id].current.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    function hasContent(content, commands) {
        return content && commands;
    }
    function buildKpiMetadataItems(kpiMetadata) {
        return (
            <div>
                <div
                    className={`${styles["metadata-entry"]} ${styles["scrollable-metadata-entry"]}`}
                >
                    {(kpiMetadata || []).map((columnMetadata, index) => {
                        const { key: column, type, name } = columnMetadata || {};

                        return (
                            <div
                                ref={refs[index]}
                                key={`${index}`}
                                className={`${styles["metadata-container"]} ${styles["darker-border"]}`}
                            >
                                <div
                                    className={`${styles["remove-button-container"]} ${styles["flex-end"]}`}
                                >
                                    <button
                                        type="button"
                                        className={styles["remove-button"]}
                                        onClick={(e) => {
                                            kpiMetadata.splice(index, 1);
                                            setEventType("stay");

                                            const metadataObj = kpiMetadata.length
                                                ? JSON.stringify({
                                                      ...metadataArrayToJson(kpiMetadata),
                                                  })
                                                : null;

                                            handleChange(e, "Metadata", metadataObj);
                                        }}
                                    >
                                        <SovosIcon>
                                            <HighlightOff />
                                        </SovosIcon>
                                    </button>
                                </div>
                                <div className={styles["flex"]}>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Column`}
                                            placeholder={i18n`Enter a column value`}
                                            value={column.split("_")[0] || ""}
                                            onKeyDown={(e) =>
                                                e.keyCode === 32 && e.preventDefault()
                                            }
                                            onChange={(e) => {
                                                delete kpiMetadata[index];
                                                kpiMetadata[index] = {
                                                    ...columnMetadata,
                                                    key: e.target.value,
                                                };
                                                setEventType("stay");
                                                handleChange(
                                                    e,
                                                    "Metadata",
                                                    JSON.stringify({
                                                        ...metadataArrayToJson(kpiMetadata),
                                                    }),
                                                );
                                            }}
                                        />
                                    </div>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Type`}
                                            placeholder={i18n`Enter a type value`}
                                            value={type || ""}
                                            onChange={(e) => {
                                                kpiMetadata[index]["type"] = e.target.value;
                                                setEventType("stay");
                                                handleChange(
                                                    e,
                                                    "Metadata",
                                                    JSON.stringify({
                                                        ...metadataArrayToJson(kpiMetadata),
                                                    }),
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className={styles["flex"]}>
                                    <div className={styles["input-field"]}>
                                        <TextInput
                                            className={styles["input"]}
                                            name={i18n`Description`}
                                            placeholder={i18n`Enter a description value`}
                                            value={name || ""}
                                            onChange={(e) => {
                                                kpiMetadata[index]["name"] = e.target.value;
                                                setEventType("stay");
                                                handleChange(
                                                    e,
                                                    "Metadata",
                                                    JSON.stringify({
                                                        ...metadataArrayToJson(kpiMetadata),
                                                    }),
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
    function buildKpiMetadataContainer(metadataItems, handleMetadataObj) {
        let tempObj = {};
        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <h5
                        className={`${styles["label"]} ${styles["no-bottom-margin"]}`}
                    >{i18n`Metadata`}</h5>
                    <div>
                        {(handleMetadataObj || []).length ? (
                            metadataItems
                        ) : (
                            <small>{i18n`No rule metadata assigned`}</small>
                        )}
                    </div>
                    <div>
                        <button
                            className={styles["add-button"]}
                            type="button"
                            onClick={(e) => {
                                tempObj = {
                                    ...metadataArrayToJson(handleMetadataObj),
                                    ...{
                                        "": {
                                            name: "",
                                            type: "",
                                        },
                                    },
                                };
                                handleChange(e, "Metadata", JSON.stringify(tempObj));
                                setEventType("scrollToContainer");
                            }}
                        >
                            <small>{i18n`[+] Add new metadata`}</small>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    function buildKpiContentContainer(contentItems, content) {
        const { query } = content || {};
        let tempObj = {};

        return (
            <div className={styles["flex"]}>
                <div className={styles["input-field"]}>
                    <h5
                        className={`${styles["label"]} ${styles["no-bottom-margin"]}`}
                    >{i18n`Content`}</h5>
                    <div>
                        {hasContent(content, query) ? (
                            contentItems
                        ) : (
                            <small>{i18n`No kpi content assigned`}</small>
                        )}
                    </div>
                    {!hasContent(content, query) ? (
                        <div>
                            <button
                                className={styles["add-button"]}
                                type="button"
                                onClick={(e) => {
                                    tempObj = {
                                        ...content,
                                        ...{
                                            query: {},
                                        },
                                    };
                                    handleChange(e, "Content", JSON.stringify(tempObj));
                                }}
                            >
                                <small>{i18n`[+] Add new content`}</small>
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
    function buildKpiContentItems(parsedContent, sqlSelectors) {
        const { query, operationType } = parsedContent || {};
        return (
            <div>
                <div className={styles["command-content-entry"]}>
                    <div
                        className={`${styles["remove-button-container"]} ${styles["flex-end"]} ${styles["selectors-remove-button"]}`}
                    >
                        <button
                            type="button"
                            className={styles["remove-button"]}
                            onClick={(e) => {
                                handleChange(e, "Content", "{}");
                            }}
                        >
                            <SovosIcon>
                                <HighlightOff />
                            </SovosIcon>
                        </button>
                    </div>
                    <div
                        className={`${styles["content-container"]} ${styles["darker-border"]} ${styles["scrollable-container"]}`}
                    >
                        {(sqlSelectors || []).map(({ id, name }, queryIndex) => {
                            return (
                                <div
                                    key={queryIndex}
                                    className={`${styles["query-content-container"]}`}
                                >
                                    <div className={styles["query-content"]}>
                                        <div
                                            className={`${styles["input-field"]} ${styles["query-key"]}`}
                                        >
                                            <TextInput
                                                style={{
                                                    width: "7.5rem",
                                                    marginTop: "0.4rem",
                                                }}
                                                value={name}
                                                disabled
                                            />
                                        </div>
                                        <div
                                            className={`${styles["input-field"]} ${styles["query-input"]}`}
                                        >
                                            <TextArea
                                                className={styles["input"]}
                                                placeholder={i18n`Enter a value`}
                                                value={(query && query[`${id}`]) || ""}
                                                onChange={(e) => {
                                                    const tempObj = parsedContent;
                                                    if (e.target.value) {
                                                        query[`${id}`] = e.target.value;
                                                    } else if (Object.prototype.hasOwnProperty.call(query, id)) {
                                                        delete query[`${id}`];
                                                    }
                                                    handleChange(
                                                        e,
                                                        "Content",
                                                        JSON.stringify({ ...tempObj }),
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div>
                        <h5 className={styles["label"]}>{i18n`Operation Types`}</h5>
                        <Dropdown
                            value={(operationTypes || []).find((item) => item.id === operationType)}
                            options={operationTypes || []}
                            className={styles["dropdown"]}
                            placeholder={i18n`-- Select Operation Type --`}
                            filter
                            onChange={(e) => {
                                const tempObj = parsedContent;
                                if (e.target.value?.id) {
                                    tempObj["operationType"] = e.target.value.id;
                                } else {
                                    delete tempObj["operationType"];
                                }
                                handleChange(e, "Content", JSON.stringify({ ...tempObj }));
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles["commands"]}>
            <div className={styles["group-content"]}>
                <div className={styles["group-content"]}>
                    {buildKpiContentContainer(kpiContentItems, parsedContent)}
                </div>
                <div className={styles["group-content"]}>
                    {buildKpiMetadataContainer(kpiMetadataItems, parsedMetadata)}
                </div>
            </div>
        </div>
    );
};

export default CommandsTab;
